import type SessionService from '@efdigital/ll-addon-auth/services/session';
import { EmptyState } from '@efdigital/ll-xceed-v2/components/application/empty-state';
import ParticipantAvatar from '@efdigital/ll-xceed-v2/components/coaching/messaging/participant-avatar.gts';
import {
  conversationPreview,
  formatMessageDate,
  hasUnreadMessages,
  latestMessage,
  type MessagingRole,
  type PartnershipConversation,
  sortConversationsByRecency,
  unreadMessageCount,
} from '@efdigital/ll-xceed-v2/utils/coaching/partnership-messages';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

interface MessagesInboxSignature {
  Args: {
    conversations: PartnershipConversation[];
    role: MessagingRole;
    programId?: string;
    cohortId?: string;
    searchInputId?: string;
  };
  Element: HTMLDivElement;
}

function conversationModels(
  conversation: PartnershipConversation,
  programId: string | undefined,
  cohortId: string | undefined
): string[] {
  if (programId && cohortId) {
    return [programId, cohortId, conversation.partnership.id!];
  }

  return [conversation.partnership.id!];
}

export default class MessagesInbox extends Component<MessagesInboxSignature> {
  @service declare session: SessionService;
  @tracked searchQuery = '';

  get conversationRoute(): string {
    return this.args.role === 'coach'
      ? 'coaching.coach.messages.conversation'
      : 'coaching.coachee.cohort.messages.conversation';
  }

  get searchFieldId(): string {
    return this.args.searchInputId ?? 'messengerSearch';
  }

  updateSearch = (event: Event) => {
    this.searchQuery = (event.target as HTMLInputElement).value;
  };

  // Re-derived from `conversation.messages` so the list reorders whenever a
  // message is sent, or the currently-open thread's poll pulls one in.
  get sortedConversations(): PartnershipConversation[] {
    return sortConversationsByRecency(this.args.conversations);
  }

  get filteredConversations(): PartnershipConversation[] {
    const query = this.searchQuery.trim().toLowerCase();
    const recencyOrder = this.sortedConversations;
    const recencyIndex = new Map(
      recencyOrder.map((conversation, index) => [conversation, index])
    );

    let conversations = recencyOrder;

    if (query) {
      conversations = conversations.filter((conversation) => {
        const preview = conversationPreview(conversation).toLowerCase();
        return (
          conversation.participant.name.toLowerCase().includes(query) ||
          preview.includes(query)
        );
      });
    }

    return [...conversations].sort((left, right) => {
      const leftUnread = this.isUnread(left);
      const rightUnread = this.isUnread(right);

      if (leftUnread !== rightUnread) {
        return leftUnread ? -1 : 1;
      }

      return recencyIndex.get(left)! - recencyIndex.get(right)!;
    });
  }

  isUnread = (conversation: PartnershipConversation): boolean => {
    return hasUnreadMessages(
      conversation,
      this.args.role,
      this.session.cached.userId!
    );
  };

  unreadCount = (conversation: PartnershipConversation): number => {
    return unreadMessageCount(
      conversation,
      this.args.role,
      this.session.cached.userId!
    );
  };

  preview = (conversation: PartnershipConversation): string => {
    return conversationPreview(conversation);
  };

  lastMessageDay = (conversation: PartnershipConversation): string => {
    const latest = latestMessage(conversation);
    return latest ? formatMessageDate(latest.createdAt) : '';
  };

  modelsFor = (conversation: PartnershipConversation): string[] => {
    return conversationModels(
      conversation,
      this.args.programId,
      this.args.cohortId
    );
  };

  <template>
    <div
      class="d-flex flex-column h-100 min-h-0 ll-bg-raised"
      data-test-messages-inbox
      ...attributes
    >
      <div class="px-3 pt-3 pb-3 flex-shrink-0 border-bottom">
        <h2 class="h4 fw-bold mb-3">Messages</h2>

        <label class="visually-hidden" for={{this.searchFieldId}}>Search
          messages</label>
        <div class="position-relative">
          <iconify-icon
            inline
            icon="ph:magnifying-glass"
            class="position-absolute top-50 translate-middle-y ms-3 text-body-secondary"
            aria-hidden="true"
          />
          <input
            id={{this.searchFieldId}}
            type="search"
            class="form-control rounded-pill border-0 bg-body-secondary ps-5 py-2"
            placeholder="Search messages"
            value={{this.searchQuery}}
            data-test-messages-search
            {{on "input" this.updateSearch}}
          />
        </div>
      </div>

      {{#if @conversations.length}}
        <div class="min-h-0 flex-shrink-0 pt-2 coaching-messages-inbox-list">
          {{#each this.filteredConversations as |conversation|}}
            <LinkTo
              @route={{this.conversationRoute}}
              @models={{this.modelsFor conversation}}
              @activeClass="active"
              class="d-block text-decoration-none text-body px-3 py-3 coaching-messages-inbox-item
                {{if
                  (this.isUnread conversation)
                  'coaching-messages-inbox-item-unread'
                }}"
              data-test-messages-inbox-item
            >
              <div class="d-flex align-items-center gap-3">
                <ParticipantAvatar
                  @participant={{conversation.participant}}
                  @size="sm"
                />

                <div class="min-w-0 flex-grow-1 overflow-hidden">
                  <div class="d-flex align-items-baseline gap-2 mb-1">
                    <span
                      class="text-truncate min-w-0 flex-grow-1
                        {{if
                          (this.isUnread conversation)
                          'fw-bold'
                          'fw-semibold'
                        }}"
                    >{{conversation.participant.name}}</span>
                    <span
                      class="small flex-shrink-0 text-nowrap
                        {{if
                          (this.isUnread conversation)
                          'text-primary fw-semibold'
                          'text-body-secondary'
                        }}"
                    >{{this.lastMessageDay conversation}}</span>
                  </div>
                  <p
                    class="small mb-0 text-truncate
                      {{if
                        (this.isUnread conversation)
                        'fw-semibold text-body'
                        'text-body-secondary'
                      }}"
                  >{{this.preview conversation}}</p>
                </div>

                {{#if (this.isUnread conversation)}}
                  <span
                    class="badge rounded-pill text-bg-primary flex-shrink-0 coaching-messages-unread-badge fw-semibold"
                    aria-label="{{this.unreadCount
                      conversation
                    }} unread messages"
                  >{{this.unreadCount conversation}}</span>
                {{/if}}
              </div>
            </LinkTo>
          {{else}}
            <p class="small text-body-secondary text-center px-3 py-4 mb-0">
              No messages match your search.
            </p>
          {{/each}}
        </div>
      {{else}}
        <div class="p-3">
          <EmptyState
            @icon="ph:chat-circle"
            @message="Messages appear here once you have shortlisted or chosen a coach."
          />
        </div>
      {{/if}}
    </div>
  </template>
}
